/**
 * Sélecteur d'agents Lauria (applications MCP accessibles).
 *
 * Responsabilités :
 * - exclure l'application courante et les agents déjà sélectionnés
 * - déléguer fetch, options et sélection à useMcpApplicationMultiSelect
 */
import { UserContext } from 'core/contexts/UserContext'
import { ApplicationMultiSelect } from 'lauria/components/common/ApplicationMultiSelect'
import { useMcpApplicationMultiSelect } from 'lauria/hooks/useMcpApplicationMultiSelect'
import { SelectedMcpApplication } from 'lauria/types/mcpApplication'
import React, { useCallback, useContext } from 'react'
import { FieldError } from 'react-hook-form'
import { useTranslation } from 'react-i18next'

type PromptAgentSelectProps = {
  currentApplicationId: number
  selectedAgents: SelectedMcpApplication[]
  setSelectedAgents: React.Dispatch<
    React.SetStateAction<SelectedMcpApplication[]>
  >
  displayDefaultOption?: boolean
  width?: string
  error?: FieldError
}

export const PromptAgentSelect: React.FC<PromptAgentSelectProps> = ({
  currentApplicationId,
  selectedAgents,
  setSelectedAgents,
  displayDefaultOption = true,
  width = 'full',
  error,
}) => {
  const { t } = useTranslation()
  const { user: currentUser } = useContext(UserContext)
  const idClient = currentUser?.idClient ?? null

  const isApplicationExcluded = useCallback(
    (application: { id: number }) =>
      application.id === currentApplicationId ||
      selectedAgents.some((agent) => agent.id === application.id),
    [currentApplicationId, selectedAgents]
  )

  const { options, isLoading, selectValue, handleSelect } =
    useMcpApplicationMultiSelect({
      idClient,
      onSelect: setSelectedAgents,
      isApplicationExcluded,
    })

  return (
    <ApplicationMultiSelect
      placeholder={t('prompts.form.advanced.agents.placeholder')}
      fieldName="mcpApplicationIds"
      options={options}
      isLoading={isLoading}
      value={selectValue}
      onChange={handleSelect}
      displayDefaultOption={displayDefaultOption}
      width={width}
      error={error}
    />
  )
}
